/**
 * Approval-toast host - the single floating surface that mirrors the live
 * approvals queue (ADR-0012). Mounted once in the app shell; renders a capped,
 * stacking column of toasts driven entirely by `ApprovalToastService` (which
 * reuses the Approvals-page fetch + SSE source - no second data source).
 *
 * Two tiers:
 *   - **Tier-1** (ask-level pending): Approve / Approve Always / Dismiss.
 *   - **Tier-2** (held send, ADR-0011): live countdown + Cancel. Auto-removes
 *     when the hold fires; cancelling aborts the send.
 *
 * The host owns layout/stacking/mobile only; all decisioning lives in the
 * service.
 */

import {
    ChangeDetectionStrategy,
    Component,
    computed,
    CUSTOM_ELEMENTS_SCHEMA,
    effect,
    inject,
    OnDestroy,
    signal,
    untracked,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { filter, map, startWith } from 'rxjs';

import { Persona } from '../../domainTypes/ddr';
import { ApprovalToast, ApprovalToastService } from '../../services/approval-toast.service';
import { AppStateService } from '../../services/app-state.service';
import { AuthService } from '../../services/auth.service';
import { ApprovalItem, DdrService, ScopeRung } from '../../services/ddr.service';
import { ProactiveActivityItem, ProactiveItemView } from '../../services/proactive-activity.service';
import {
    approvalActionPhrase,
    approvalBody,
    approvalCcEmails,
    approvalSubject,
    approvalToolLabel,
    humanizeScopeTier,
    splitRecipientDisplay,
} from '../ddr-cards/approval-detail';
import { EmailReviewDialog } from '../email-review-dialog/email-review-dialog';
import { HoldDrainBar } from '../hold-drain-bar/hold-drain-bar';
import { ScopeChooser } from '../scope-chooser/scope-chooser';

@Component({
    selector: 'app-approval-toast-host',
    imports: [EmailReviewDialog, HoldDrainBar, RouterLink, ScopeChooser],
    templateUrl: './approval-toast-host.html',
    styleUrl: './approval-toast-host.css',
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ApprovalToastHost implements OnDestroy {
    private readonly toasts = inject(ApprovalToastService);
    private readonly appState = inject(AppStateService);
    private readonly auth = inject(AuthService);
    private readonly ddr = inject(DdrService);
    private readonly router = inject(Router);

    private readonly approvalsRouteActive = toSignal(
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            startWith(null),
            map(() => this.router.url.startsWith('/chat/workspace/approvals') || this.router.url.includes('view=approvals'))
        ),
        { initialValue: this.router.url.startsWith('/chat/workspace/approvals') || this.router.url.includes('view=approvals') }
    );

    private readonly ddrRoomRouteActive = toSignal(
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            startWith(null),
            map(() => this.isDdrRoomUrl(this.router.url))
        ),
        { initialValue: this.isDdrRoomUrl(this.router.url) }
    );

    private readonly workspaceRouteActive = toSignal(
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            startWith(null),
            map(() => this.router.url.startsWith('/chat/workspace'))
        ),
        { initialValue: this.router.url.startsWith('/chat/workspace') }
    );

    /** Ticks on every completed navigation; used to collapse the mobile stack so
     *  each screen starts closed rather than inheriting the prior screen's state. */
    private readonly navigationTick = toSignal(
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            map(() => this.router.url)
        ),
        { initialValue: this.router.url }
    );

    // The approval surface is a DDR concept, shown inside a DDR room/DM and on
    // workspace pages — never on the bare main CIQ chat. The Approvals page
    // itself is excluded via suppressToasts (the full queue is already there).
    readonly toastSurfaceActive = computed(
        () => this.auth.isAuthenticated() && this.auth.ddrEnabled() && (this.ddrRoomRouteActive() || this.workspaceRouteActive())
    );
    readonly suppressToasts = computed(
        () => !this.toastSurfaceActive() || this.appState.approvalsViewActive() || this.approvalsRouteActive()
    );

    /** Mobile: on workspace pages (no composer) the floating pill is the toggle
     *  — always shown (like the DDR-room bar), never hidden while expanded.
     *  Inside a DDR room the ddr-room-panel's blue bar is the trigger instead. */
    readonly showPill = computed(() => this.isNarrow() && this.workspaceRouteActive() && !this.ddrRoomRouteActive());

    /** The toast stack shows on desktop always; on mobile only once a trigger
     *  (bar or pill) has expanded it. */
    readonly stackVisible = computed(() => !this.isNarrow() || this.appState.mobileApprovalsOpen());

    /** Whether the mobile stack is currently expanded (drives the pill chevron). */
    readonly approvalsOpen = this.appState.mobileApprovalsOpen;

    /** Suppress toasts when the Approvals panel is open - the full queue is already visible. */
    readonly visibleToasts = computed(() => (this.suppressToasts() ? [] : this.toasts.visibleToasts()));
    readonly overflowCount = computed(() => (this.suppressToasts() ? 0 : this.toasts.overflowCount()));
    readonly approvalCount = computed(() => (this.suppressToasts() ? 0 : this.toasts.totalCount()));
    readonly extraApprovalCount = computed(() => this.overflowCount());
    /** Small screens collapse the stack into a single "N approvals waiting"
     *  pill - full toasts covered most of the viewport at phone widths. */
    private readonly narrowQuery = window.matchMedia('(max-width: 640px)');
    readonly isNarrow = signal(this.narrowQuery.matches);
    private readonly onNarrowChange = (e: MediaQueryListEvent): void => this.isNarrow.set(e.matches);
    readonly bulkBusy = signal<'approve' | 'reject' | null>(null);
    readonly bulkConfirmAction = signal<'approve' | 'reject' | null>(null);
    readonly expandedPreviewIds = signal<Set<string>>(new Set());
    readonly personas = signal<Persona[]>([]);

    /** Toast id whose blast-radius chooser is currently open (null = none). */
    readonly chooserOpenId = signal<string | null>(null);

    /** The approval whose rich detail modal is open (#256 View), mapped onto the
     *  proactive card/item shape so it reuses EmailReviewDialog. All null = closed. */
    readonly viewingItem = signal<ApprovalItem | null>(null);
    readonly viewCard = signal<ProactiveActivityItem | null>(null);
    readonly viewItemView = signal<ProactiveItemView | null>(null);

    constructor() {
        this.narrowQuery.addEventListener('change', this.onNarrowChange);
        effect(() => {
            // Track ONLY toastSurfaceActive. The work below reads other signals
            // (loadPersonas reads `personas`); if tracked, every personas.set()
            // re-runs the effect → loadPersonas → personas.set(new array) → …
            // an infinite microtask loop whenever the workspace has no personas.
            const surfaceActive = this.toastSurfaceActive();
            untracked(() => {
                if (surfaceActive) {
                    void this.loadPersonas();
                    this.toasts.start();
                } else {
                    this.toasts.stop();
                }
            });
        });
        // Collapse the mobile stack when there's nothing left to show or the
        // surface is gone (e.g. after approving the last item).
        effect(() => {
            if (this.visibleToasts().length === 0 || !this.toastSurfaceActive()) {
                this.appState.setMobileApprovalsOpen(false);
            }
        });
        // Also collapse on every navigation, so each screen starts closed
        // (behind the bar or pill) rather than inheriting the prior screen.
        effect(() => {
            this.navigationTick();
            this.appState.setMobileApprovalsOpen(false);
        });
    }

    /** Mobile: the floating pill toggles the stack in place (mirrors the bar). */
    toggleMobileStack(): void {
        this.appState.toggleMobileApprovals();
    }

    ngOnDestroy(): void {
        // The host lives for the lifetime of the shell; stop only on teardown
        // (e.g. logout swapping the root component) so we don't leak the SSE sub.
        this.narrowQuery.removeEventListener('change', this.onNarrowChange);
        this.toasts.stop();
    }

    private isDdrRoomUrl(url: string): boolean {
        return url.startsWith('/chat/ddr/room') || url.startsWith('/chat/ddr/dm');
    }

    trackById(_index: number, toast: ApprovalToast): string {
        return toast.item.id;
    }

    /** Plain-language headline ("Send email", "Reply to email", …). */
    toolLabel(item: ApprovalItem): string {
        return approvalToolLabel(item.tool_id);
    }

    approvalTitle(item: ApprovalItem): string {
        const who = this.personaFirstName(item);
        // A held item is already approved and auto-sending (cancellable), NOT
        // waiting on the user - otherwise the countdown + Cancel contradict a
        // "wants approval" headline.
        if (item.status === 'held') {
            return `${who} is auto-sending ${this.messageType(item)}. Cancel to stop`;
        }
        // The phrase carries its own verb ("send email", "use Gmail: Send
        // Email") - a hardcoded "to send" read as garbage for MCP tools.
        return `${who} wants approval to ${approvalActionPhrase(item.tool_id)}`;
    }

    personaFirstName(item: ApprovalItem): string {
        const name = this.personas()
            .find(p => p.id === item.persona_id)
            ?.display_name.trim();
        return name?.split(/\s+/)[0] || 'DDR';
    }

    messageType(item: ApprovalItem): string {
        switch (item.tool_id) {
            case 'send_email':
                return 'email';
            case 'email_reply_thread':
                return 'email reply';
            case 'teams_send_chat_to_person':
                return 'teams chat';
            case 'teams_send_chat_message':
                return 'teams message';
            case 'teams_send_channel_message':
                return 'teams channel post';
            case 'teams_create_group_chat':
                return 'teams group chat';
            default:
                return this.toolLabel(item).toLowerCase();
        }
    }

    private async loadPersonas(): Promise<void> {
        // untracked: this runs from an effect — a tracked read here would make
        // the effect depend on `personas` and loop on every set() (see ctor).
        if (untracked(this.personas).length) {
            return;
        }
        try {
            this.personas.set(await this.ddr.listPersonas());
        } catch {
            this.personas.set([]);
        }
    }

    /** Backend-resolved human summary ("To: Ryan Rapini …"), when present and
     *  not degraded to the raw tool id. */
    summary(item: ApprovalItem): string | null {
        if (
            (item.tool_id.startsWith('teams_') || item.tool_id === 'send_email' || item.tool_id === 'email_reply_thread') &&
            this.previewBody(item)
        ) {
            return null;
        }
        const raw = item.summary?.trim();
        if (!raw || raw === item.tool_id) {
            return null;
        }
        return raw;
    }

    targetLabel(item: ApprovalItem): string | null {
        const label = item.target_label?.trim();
        return label && item.target_type !== 'unknown' ? label : null;
    }

    /** Toasts whose recipient line the user expanded past "+N more" (#278). */
    readonly expandedRecipients = signal<ReadonlySet<string>>(new Set());

    expandRecipients(id: string): void {
        this.expandedRecipients.update(set => {
            const next = new Set(set);
            next.add(id);
            return next;
        });
    }

    /** Recipient line bounded to a few names + "+N more" so a wide send
     *  doesn't wall the toast; expands on click. */
    targetLine(item: ApprovalItem): { shown: string; hidden: number } | null {
        const display = this.targetLabel(item);
        if (!display) {
            return null;
        }
        if (this.expandedRecipients().has(item.id)) {
            return { shown: display, hidden: 0 };
        }
        return splitRecipientDisplay(display);
    }

    targetPrefix(item: ApprovalItem): string {
        switch (item.target_type) {
            case 'calendar_attendees':
                return 'Attendees:';
            case 'teams_chat':
                return 'Chat:';
            case 'teams_channel':
                return 'Channel:';
            case 'email_recipient':
            case 'teams_user':
            default:
                return 'To:';
        }
    }

    // Layer-2 fields (#246) ride on payload.meta so the toast matches the rail/queue.
    whyYou(item: ApprovalItem): string | null {
        const meta = item.payload?.['meta'] as { why_you?: string } | undefined;
        return meta?.why_you?.trim() || null;
    }

    recommendationText(item: ApprovalItem): string | null {
        const meta = item.payload?.['meta'] as { recommendation?: string } | undefined;
        return meta?.recommendation?.trim() || null;
    }

    previewBody(item: ApprovalItem): string | null {
        return approvalBody(item);
    }

    previewSubject(item: ApprovalItem): string | null {
        return approvalSubject(item);
    }

    isEmailApproval(item: ApprovalItem): boolean {
        return item.tool_id === 'send_email' || item.tool_id === 'email_reply_thread';
    }

    isPreviewExpandable(item: ApprovalItem): boolean {
        const body = this.previewBody(item);
        if (!body) {
            return false;
        }
        const subject = this.previewSubject(item) ?? '';
        const text = `${subject}\n${body}`.trim();
        return text.split(/\r?\n/).length > 3 || text.length > 120;
    }

    isPreviewExpanded(id: string): boolean {
        return this.expandedPreviewIds().has(id);
    }

    togglePreview(item: ApprovalItem): void {
        if (!this.isPreviewExpandable(item)) {
            return;
        }
        if (this.isPreviewExpanded(item.id)) {
            this.collapsePreview(item.id);
        } else {
            this.expandPreview(item.id);
        }
    }

    expandPreview(id: string): void {
        this.expandedPreviewIds.update(ids => new Set(ids).add(id));
    }

    collapsePreview(id: string): void {
        this.expandedPreviewIds.update(ids => {
            const next = new Set(ids);
            next.delete(id);
            return next;
        });
    }

    /** Short risk/scope chip; null when not worth surfacing. */
    scopeLabel(item: ApprovalItem): string | null {
        return humanizeScopeTier(item.scope_tier);
    }

    /** Applicable blast-radius rungs for the chooser (backend-enumerated,
     *  narrow→broad). Falls back to the single cell the action rode when an
     *  older payload has no `scope_rungs`. */
    rungs(item: ApprovalItem): ScopeRung[] {
        return item.scope_rungs ?? [{ scope_tier: item.scope_tier, label: item.scope_tier, locked: false, reason: null }];
    }

    onApprove(id: string): void {
        void this.toasts.approve(id);
    }

    onApproveAll(): void {
        if (this.bulkBusy()) {
            return;
        }
        this.bulkBusy.set('approve');
        void this.toasts.approveAll().finally(() => this.bulkBusy.set(null));
    }

    onRejectAll(): void {
        if (this.bulkBusy()) {
            return;
        }
        this.bulkBusy.set('reject');
        void this.toasts.rejectAll().finally(() => this.bulkBusy.set(null));
    }

    onDismissAll(): void {
        this.chooserOpenId.set(null);
        this.toasts.dismissAll();
    }

    askBulkConfirm(action: 'approve' | 'reject'): void {
        if (this.bulkBusy()) {
            return;
        }
        this.bulkConfirmAction.set(action);
    }

    bulkConfirmLabel(): string {
        // Reject All clears EVERY pending approval server-side - including ones
        // hidden by the 3-toast cap, ones dismissed earlier, and everything on
        // the Approvals page - so its confirm text discloses that blast radius.
        switch (this.bulkConfirmAction()) {
            case 'approve':
                return 'approve all';
            case 'reject':
                return 'reject ALL pending approvals, including any dismissed or not shown here';
            default:
                return '';
        }
    }

    onCancelBulkConfirm(): void {
        this.bulkConfirmAction.set(null);
    }

    onConfirmBulkAction(): void {
        const action = this.bulkConfirmAction();
        this.bulkConfirmAction.set(null);
        switch (action) {
            case 'approve':
                this.onApproveAll();
                break;
            case 'reject':
                this.onRejectAll();
                break;
        }
    }

    /** Open the blast-radius chooser for this toast instead of promoting
     *  immediately - the user picks the scope, then confirms. */
    onApproveAlways(id: string): void {
        this.chooserOpenId.set(id);
    }

    /** Confirm the chooser: promote at the chosen scope + fire the action. */
    onScopeConfirm(id: string, scopeTier: string): void {
        this.chooserOpenId.set(null);
        void this.toasts.approveAlways(id, scopeTier);
    }

    onScopeCancel(): void {
        this.chooserOpenId.set(null);
    }

    onCancel(id: string): void {
        void this.toasts.cancel(id);
    }

    onReject(id: string): void {
        void this.toasts.reject(id);
    }

    onDismiss(id: string): void {
        if (this.chooserOpenId() === id) {
            this.chooserOpenId.set(null);
        }
        this.toasts.dismiss(id);
    }

    // ── View modal (#256) — reuse EmailReviewDialog for full detail ──────

    /** Open the rich detail modal for a toast's approval, mapped onto the proactive
     *  card/item shape (full body, envelope, why-you / recommendation, draft). */
    openView(item: ApprovalItem): void {
        const p = this.personas().find(x => x.id === item.persona_id);
        this.viewCard.set({
            id: item.id,
            kind: 'approval',
            source: item.tool_id,
            persona_id: item.persona_id ?? null,
            persona_display_name: p?.display_name ?? null,
            persona_archetype: p?.archetype ?? null,
            title: this.approvalTitle(item),
            teaser: null,
            payload: item.payload ?? {},
            created_at: item.created_at ?? null,
        });
        this.viewItemView.set({
            text: this.summary(item) ?? this.approvalTitle(item),
            subject: this.previewSubject(item) ?? undefined,
            from: this.targetLabel(item) ?? undefined,
            cc: approvalCcEmails(item),
            draft_reply: this.previewBody(item) ?? '',
            why_you: this.whyYou(item) ?? undefined,
            recommendation: this.recommendationText(item) ?? undefined,
            meta: this.targetLabel(item) ? `${this.targetPrefix(item)} ${this.targetLabel(item)}` : undefined,
        });
        this.viewingItem.set(item);
    }

    closeView(): void {
        this.viewingItem.set(null);
        this.viewCard.set(null);
        this.viewItemView.set(null);
    }

    onViewApprove(): void {
        const item = this.viewingItem();
        if (item) {
            this.onApprove(item.id);
            this.closeView();
        }
    }

    onViewReject(): void {
        const item = this.viewingItem();
        if (item) {
            this.onReject(item.id);
            this.closeView();
        }
    }

    /** Scope picked in the review modal's in-modal chooser: promote that cell +
     *  approve, then close. (The old flow opened the toast chooser and closed
     *  the modal — a no-op when toasts are suppressed on the approvals route.) */
    onViewScopeConfirm(scopeTier: string): void {
        const item = this.viewingItem();
        if (item) {
            void this.toasts.approveAlways(item.id, scopeTier);
            this.closeView();
        }
    }
}
